import { useMemo, useContext } from 'react'
import { useFilterStore } from '../stores/useFilterStore'
import { useZustand } from './useZustand'
import {
    useContextsStore,
    selectOwnerContexts,
    selectIsPublicView,
    selectGraphOwnerUsername,
} from '../stores/useContextsStore'
import { useFindStore } from '../stores/useFindStore'
import { useGraphStore } from '../stores/useGraphStore'
import usePageInfo from './usePageInfo'
import { UserContext } from '../UserContext'
import { getGroupColor } from '../utils/graphColor'

/**
 * Map frontend comparison mode titles to backend API mode values
 * @param {string} frontendMode - Frontend mode title from useCompareMode
 * @returns {string|null} - Backend API mode value or null
 */
const mapComparisonModeToBackend = frontendMode => {
    const modeMapping = {
        Combination: 'merge',
        Difference: 'difference',
        Intersection: 'intersection',
        Embedding: 'merge', // Not supported in backend
    }
    return modeMapping[frontendMode] || null
}

/**
 * Custom hook to provide selectedTopics, selectedConcepts, and selectedGraph for AI Chat Widget
 * Transforms data from filter store and zustand to match ChatWidget interface requirements
 */
export const useAiChatWidgetData = () => {
    const {
        pinnedNodes,
        highlight,
        isAnyFilterActive,
        isAnyRecalculateActive,
        isAnyHighlightActive,
    } = useFilterStore()

    const stopWords = useFilterStore(state => state.stopWords)
    const manuallySelectedStatements = useFilterStore(
        state => state.selectedStatements
    )

    const {
        currentContext,
        additionalContexts,
        insightClusters,
        allClusters,
        forceRequestMode,
        comparisonMode,
        doNotSaveGraph,
    } = useZustand()
    const { label } = usePageInfo()
    // Viewer (for telemetry callbacks if any) vs. graph-owner (the field
    // the AI chat needs to address the correct backend graph). Today's bug
    // was using viewer.username for both; the fix is to send graphOwner.
    const { username: viewerUsername } = useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)
    const graphOwnerUsername = useContextsStore(selectGraphOwnerUsername)
    const contexts = useContextsStore(selectOwnerContexts)

    const filteredStatements = useFilterStore(state => state.filteredStatements)

    const findStoreTerms = useFindStore(state => state.terms)
    const searchTerms = findStoreTerms.join(',')

    // Manual click-selection of statements. Mirrors the backend's context
    // budget (maxOnlyContextSize in lib/graph.js): a selection that fits is
    // sent alone — no topics; one that overflows keeps the covered topics
    // (set by the graph's selectCommunityFilter cascade) as the frame for the
    // sampled selection.
    const manualSelectionActive = manuallySelectedStatements.size > 0
    const manualSelectionOverflows = useMemo(() => {
        if (!manualSelectionActive) return false
        const totalLength = filteredStatements
            .filter(statement => manuallySelectedStatements.has(statement.uid))
            .reduce((sum, statement) => sum + (statement.text?.length || 0), 0)
        return totalLength > 30000
    }, [manualSelectionActive, manuallySelectedStatements, filteredStatements])

    /**
     * Transform topics from highlight filter to ChatWidget Topic format
     * Each topic gets its community-based color from the graph palette
     */
    const selectedTopicsFromHighlight = useMemo(() => {
        // Color comes from the graph's own scheme (from @infranodus/infranodus-graph/colors): the
        // topic id IS the community group, mapped through schemePaired exactly
        // like the renderer. The old Sigma `graphFactory.getPalette()` lookup
        // was unrelated and defaulted every topic to placeholder purple.
        return highlight.topics.map(topic => ({
            id: topic.id,
            name: topic.aiName,
            color: getGroupColor(topic.id),
            concepts: topic.nodes,
        }))
    }, [highlight.topics])

    // A small manual selection is sent without topics so the AI works from
    // just those statements; an overflowing one keeps its covered topics.
    const selectedTopics =
        manualSelectionActive && !manualSelectionOverflows
            ? []
            : selectedTopicsFromHighlight

    /**
     * Transform pinnedNodes to ChatWidget Concept format
     * Each concept gets its community-based color from the graph palette
     */
    const selectedConcepts = useMemo(() => {
        const getCommunityOfNode = useGraphStore.getState().getCommunityOfNode
        return pinnedNodes.map(node => {
            const nodeName = node.name || node.id || node

            // A node's color is its community's color in the graph scheme
            // (@infranodus/infranodus-graph/colors), matching how the renderer colors the node.
            const community = getCommunityOfNode(nodeName)
            const color = getGroupColor(community, node.color || '#8c04ff')

            return {
                id: node.id || node,
                name: nodeName,
                color: color,
            }
        })
    }, [pinnedNodes])

    /**
     * Transform current context to selectedGraph format for ChatWidget
     * Creates graph object with context information
     * Includes additionalContexts for multi-context support
     * Handles private graphs (doNotSaveGraph=true) that aren't in the contexts list
     */
    const selectedGraph = useMemo(() => {
        // Admin read-only viewer: adminGraphData primed a private-cache slot
        // under the admin's own uid (routes/admin.js), announced via
        // __ADMIN_VIEW__.aiContextName — so the widget runs the private-graph
        // AI path, same as doNotSaveGraph below. Gated on currentContext,
        // which processStatements sets only after the admin data endpoint has
        // responded: the widget cannot fire its insights request before the
        // cache slot exists.
        const adminView =
            typeof window !== 'undefined' ? window.__ADMIN_VIEW__ : undefined
        if (adminView?.adminView) {
            if (!adminView.aiContextName || !currentContext) {
                return null
            }
            return {
                id: `admin_${adminView.graphOwner?.id}_${adminView.contextName}`,
                graphName: adminView.aiContextName,
                promptGraph: '',
                promptContext: '',
                isPrivate: true,
                additionalContexts: [],
                comparisonMode: null,
            }
        }
        if (label === 'Top Graphs Synthesis') {
            return {
                id: 'SYNTHESIS_GRAPHS',
                graphName: 'SYNTHESIS_GRAPHS',
                promptGraph: '',
                promptContext: '',
                comparisonMode: mapComparisonModeToBackend(comparisonMode),
            }
        }
        if (label === 'Top Concepts Synthesis') {
            return {
                id: 'SYNTHESIS_CONCEPTS',
                graphName: 'SYNTHESIS_CONCEPTS',
                promptGraph: '',
                promptContext: '',
                comparisonMode: mapComparisonModeToBackend(comparisonMode),
            }
        }
        if (label === 'Search All Graphs') {
            // Wire encoding `SEARCH_GRAPHS:term1,term2`; comma separator preserves
            // multi-word concepts like "tai chi". Decoded by `decodeSearchTerms`.
            return {
                id: 'SEARCH_GRAPHS',
                graphName: `SEARCH_GRAPHS:${searchTerms}`,
                promptGraph: '',
                promptContext: '',
                comparisonMode: mapComparisonModeToBackend(comparisonMode),
            }
        }
        // Handle private graphs first - they may not have context in list
        // and may be viewed from import page (different label)
        if (doNotSaveGraph && currentContext) {
            return {
                id: `private_${currentContext}`,
                graphName: currentContext,
                promptGraph: '',
                promptContext: '',
                isPrivate: true,
                additionalContexts: additionalContexts || [],
                comparisonMode: mapComparisonModeToBackend(comparisonMode),
            }
        }

        if (!currentContext || !label) {
            return null
        }

        if (!contexts || contexts.length === 0) {
            return null
        }

        const contextData = contexts.find(c => c.name === currentContext)
        if (!contextData) {
            return null
        }

        return {
            id: contextData.id,
            graphName: contextData.name,
            promptGraph: '',
            promptContext: '',
            additionalContexts: additionalContexts || [],
            comparisonMode: mapComparisonModeToBackend(comparisonMode),
            isPublic: isPublic || false,
            // Fix for C-G6: send the graph-OWNER's username (mona11 when an
            // anonymous visitor views mona11's public graph), not the
            // viewer's. Falls back to viewer for the owner-viewing-own-graph
            // case where graphOwner has just been resolved as the viewer.
            username: graphOwnerUsername || viewerUsername || null,
        }
    }, [
        currentContext,
        contexts,
        isPublic,
        graphOwnerUsername,
        viewerUsername,
        additionalContexts,
        comparisonMode,
        doNotSaveGraph,
        label,
        searchTerms,
    ])

    // filteredStatementIds: Only return when recalculate filters are active
    // These IDs are used to rebuild the graph on backend
    const filteredStatementIds = useMemo(() => {
        if (!isAnyRecalculateActive()) {
            return null
        }
        return filteredStatements.map(statement => statement.uid)
    }, [filteredStatements, isAnyRecalculateActive])

    // selectedStatementIds: Visible statements for AI prioritization
    // Return when highlight filters are active OR when pinnedNodes exist.
    // Statements the user explicitly selected in the panel (highlighted in the
    // graph) take precedence over the visible/filtered set — the backend
    // prioritizes them the same way (and samples them by community when there
    // are too many for the context window).
    const selectedStatementIds = useMemo(() => {
        if (manuallySelectedStatements.size > 0) {
            return Array.from(manuallySelectedStatements)
        }
        const hasPinnedNodes = pinnedNodes && pinnedNodes.length > 0
        if (!isAnyHighlightActive() && !hasPinnedNodes) {
            return null
        }
        return filteredStatements
            .filter(
                statement =>
                    statement.visible === true && statement.grayedOut !== true
            )
            .map(statement => statement.uid)
    }, [
        filteredStatements,
        isAnyHighlightActive,
        pinnedNodes,
        manuallySelectedStatements,
    ])

    return {
        selectedTopics,
        selectedConcepts,
        selectedGraph,
        insightClusters,
        allClusters,
        stopWords,
        forceRequestMode,
        filteredStatementIds,
        selectedStatementIds,
        statementsSelectionMode: manualSelectionActive ? 'manual' : null,
        isPublic,
    }
}
